import { PaperClipOutlined } from '@ant-design/icons';
import { Spin, Table, Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { BsReceipt } from 'react-icons/bs';
import { HiOutlineLockClosed, HiOutlinePlus } from 'react-icons/hi';

import { PanelLayout } from '../../components/PanelLayout';
import { useAuth } from '../../hooks/useAuth';
import { useMobileVersion } from '../../hooks/useMobileVersion';
import { getAllSolicitations } from '../../operations/solicitations';
import CreateSolicitation from './CreateSolicitation';
import {
  AttachmentBadge,
  Container,
  CreateButton,
  DateCell,
  EmptyState,
  HeaderActions,
  HeaderLeft,
  HeaderTop,
  MobileAttachment,
  MobileCard,
  MobileCardBody,
  MobileCardDate,
  MobileCardFooter,
  MobileCardMeta,
  MobileCardProtocol,
  MobileCardSub,
  MobileCardSummary,
  MobileCardTop,
  MobileFab,
  MobileList,
  PageSubtitle,
  PageTitle,
  ProtocolCell,
  SearchInput,
  StatusBadge,
  SummaryCell,
  SummaryMain,
  SummarySub,
  TableStyled,
} from './styles';
import ViewSolicitation from './ViewSolicitation';

const { Column } = Table;

const getSummary = (row) => {
  var customData = row.custom_data;

  if (!customData) {
    return { main: row.solicitation_type, sub: row.description || '' };
  }

  if (customData.upload_file) {
    return {
      main: 'Planilha enviada',
      sub: customData.upload_file.original_name || '',
    };
  }

  if (Array.isArray(customData) && customData.length > 0) {
    var count = customData.length;
    var names = customData
      .filter(function(item) { return item.name; })
      .map(function(item) { return item.name; })
      .slice(0, 3);

    return {
      main: count + (count === 1 ? ' nota fiscal' : ' notas fiscais'),
      sub: names.join(', ') + (customData.length > 3 ? '...' : ''),
    };
  }

  return { main: row.solicitation_type, sub: row.description || '' };
};

export const Solicitations = () => {
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { user } = useAuth();
  const isMobile = useMobileVersion();

  useEffect(() => {
    if (!user || !user.allow_nf) return;

    setIsLoading(true);
    getAllSolicitations()
      .then((response) =>
        setRows(
          response.map((row) => ({
            ...row,
          }))
        )
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, [user && user.allow_nf]);

  const getStatusBadge = (statusKey) => {
    const status = statusKey === 1 ? 'completed' : 'pending';
    const label = statusKey === 1 ? 'Concluído' : 'Pendente';
    return <StatusBadge $status={status}>{label}</StatusBadge>;
  };

  const filteredData = rows.filter((row) => {
    if (!filter) return true;
    var summary = getSummary(row);
    var searchable = [
      row.protocol || '',
      row.description || '',
      summary.main,
      summary.sub,
      row.solicitation_type,
    ].join(' ').toLowerCase();
    return searchable.includes(filter);
  });

  const handleRowClick = (record) => {
    setSelectedRow(record);
  };

  const renderEmptyState = () => (
    <EmptyState>
      <BsReceipt />
      <h3>Nenhuma nota fiscal solicitada</h3>
      <p>Solicite a emissão de notas fiscais para seus serviços prestados.</p>
      <CreateButton onClick={() => setIsCreateModalOpen(true)}>
        <HiOutlinePlus />
        Solicitar Emissão
      </CreateButton>
    </EmptyState>
  );

  if (!user || !user.allow_nf) {
    return (
      <PanelLayout>
        <Container>
          <EmptyState>
            <HiOutlineLockClosed />
            <h3>Acesso restrito</h3>
            <p>Sua conta não possui permissão para emissão de notas fiscais. Entre em contato com seu contador para habilitar este recurso.</p>
          </EmptyState>
        </Container>
      </PanelLayout>
    );
  }

  const renderMobileCards = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (filteredData.length === 0) {
      return filter
        ? <EmptyState><p style={{ margin: 0 }}>Nenhum resultado encontrado</p></EmptyState>
        : renderEmptyState();
    }

    return (
      <MobileList>
        {filteredData.map((row) => {
          var summary = getSummary(row);
          var filesCount = row.files_count || 0;
          return (
            <MobileCard key={row.id} onClick={() => handleRowClick(row)}>
              <MobileCardTop>
                <MobileCardProtocol>{row.protocol || '—'}</MobileCardProtocol>
                <MobileCardDate>{moment(row.created_at).format('DD/MM/YYYY')}</MobileCardDate>
              </MobileCardTop>
              <MobileCardBody>
                <MobileCardSummary>{summary.main}</MobileCardSummary>
                {summary.sub && <MobileCardSub>{summary.sub}</MobileCardSub>}
              </MobileCardBody>
              <MobileCardFooter>
                <MobileCardMeta>
                  {getStatusBadge(row.status)}
                  {filesCount > 0 && (
                    <MobileAttachment>
                      <PaperClipOutlined />
                      {filesCount}
                    </MobileAttachment>
                  )}
                </MobileCardMeta>
              </MobileCardFooter>
            </MobileCard>
          );
        })}
      </MobileList>
    );
  };

  const renderDesktopTable = () => (
    <TableStyled
      dataSource={filteredData}
      rowKey="id"
      pagination={{
        position: ['bottomCenter'],
        showSizeChanger: false,
        pageSize: 10,
      }}
      loading={isLoading}
      onRow={(record) => ({
        onClick: () => handleRowClick(record),
      })}
      locale={{
        emptyText: filter ? 'Nenhum resultado encontrado' : 'Nenhuma solicitação',
      }}
    >
      <Column
        width={150}
        title="Protocolo"
        key="protocol"
        dataIndex="protocol"
        render={(text) => (
          <ProtocolCell>{text || '—'}</ProtocolCell>
        )}
      />
      <Column
        width={120}
        title="Data"
        key="created_at"
        dataIndex="created_at"
        render={(text) => (
          <DateCell>{moment(text).format('DD/MM/YYYY')}</DateCell>
        )}
        sorter={(a, b) => moment(a.created_at).unix() - moment(b.created_at).unix()}
        defaultSortOrder="descend"
      />
      <Column
        title="Resumo"
        key="summary"
        render={(_, row) => {
          var summary = getSummary(row);
          return (
            <SummaryCell>
              <SummaryMain>{summary.main}</SummaryMain>
              {summary.sub && <SummarySub>{summary.sub}</SummarySub>}
            </SummaryCell>
          );
        }}
        ellipsis
      />
      <Column
        width={60}
        title=""
        key="files"
        align="center"
        render={(_, row) => {
          var count = row.files_count || 0;
          if (count === 0) return null;
          return (
            <Tooltip title={count + (count === 1 ? ' arquivo' : ' arquivos')}>
              <AttachmentBadge>
                <PaperClipOutlined />
                <span>{count}</span>
              </AttachmentBadge>
            </Tooltip>
          );
        }}
      />
      <Column
        align="center"
        width={120}
        title="Status"
        key="status"
        dataIndex="status"
        filters={[
          { text: 'Pendente', value: 0 },
          { text: 'Concluído', value: 1 },
        ]}
        onFilter={(value, record) => record.status === value}
        render={(text) => getStatusBadge(text)}
      />
    </TableStyled>
  );

  return (
    <PanelLayout>
      <ViewSolicitation
        solicitation={selectedRow}
        onClose={() => setSelectedRow(null)}
        onDelete={(id) => {
          setRows(rows.filter((row) => row.id !== id));
          setSelectedRow(null);
        }}
      />
      <Container>
        <HeaderTop>
          <HeaderLeft>
            <PageTitle>Notas Fiscais</PageTitle>
            {!isMobile && (
              <PageSubtitle>Gerencie suas notas fiscais eletrônicas.</PageSubtitle>
            )}
          </HeaderLeft>
          <HeaderActions>
            <SearchInput
              onChange={(event) => setFilter(event.target.value.toLowerCase())}
              placeholder="Pesquisar..."
            />
            {!isMobile && (
              <CreateButton onClick={() => setIsCreateModalOpen(true)}>
                <HiOutlinePlus />
                Solicitar NF-e
              </CreateButton>
            )}
          </HeaderActions>
        </HeaderTop>

        {!isLoading && rows.length === 0
          ? renderEmptyState()
          : isMobile
            ? renderMobileCards()
            : renderDesktopTable()
        }
      </Container>

      {isMobile && (
        <MobileFab onClick={() => setIsCreateModalOpen(true)}>
          <HiOutlinePlus />
        </MobileFab>
      )}

      <CreateSolicitation
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        setRows={setRows}
        rows={rows}
      />
    </PanelLayout>
  );
};
